import { Button } from 'components/Button';
import { DataTable, DataTableMessageRow } from 'components/DataTable';
import { Pill } from 'components/ManagementCard';
import { summaryManagementStrings } from 'constants/strings';
import moment from 'moment-timezone';
import type {
  TLegacySummaryRow,
  TSummaryMode,
  TSummaryRow,
} from 'types/summary';
import {
  formatSummaryClickRate,
  formatSummaryCount,
  resolveSummaryListMessage,
  resolveUnavailableRecipientBadge,
  resolveUnknownRecipientBadge,
  summaryStatusTone,
} from './summaryDisplay';

type SummaryDisplayRow = TSummaryRow | TLegacySummaryRow;

type SummaryTableProps = {
  mode: TSummaryMode;
  rows: SummaryDisplayRow[];
  loading: boolean;
  /** 取得失敗。空一覧を「該当0件」と誤読させないため空メッセージを出さない */
  hasError?: boolean;
  onSelect: (row: SummaryDisplayRow, e?: React.MouseEvent) => void;
};

function formatDate(iso: string): string {
  return moment(iso).tz('Asia/Tokyo').format('YYYY年M月D日 HH:mm');
}

function formatNumber(value: number | null): string {
  return value == null
    ? summaryManagementStrings.unavailableMetric
    : value.toLocaleString('ja-JP');
}

function formatLegacyRate(row: TLegacySummaryRow): string {
  if (row.deliveryCount <= 0) return summaryManagementStrings.unavailableMetric;
  return `${Math.round(
    (row.legacyOpenedCount / row.deliveryCount) * 100,
  )}%`;
}

function isLegacyRow(row: SummaryDisplayRow): row is TLegacySummaryRow {
  return 'sentAt' in row;
}

export function SummaryTable({
  mode,
  rows,
  loading,
  hasError = false,
  onSelect,
}: SummaryTableProps) {
  const columnCount = mode === 'CLICK' ? 8 : 5;
  const listMessage = resolveSummaryListMessage({
    mode,
    loading,
    hasError,
    rowCount: rows.length,
  });
  let body: JSX.Element | JSX.Element[];

  if (listMessage !== null) {
    body = (
      <DataTableMessageRow colSpan={columnCount} message={listMessage} />
    );
  } else if (mode === 'LEGACY_OPEN') {
    body = rows
      .filter(isLegacyRow)
      .map((row) => (
        <LegacySummaryTableRow key={row.id} row={row} onSelect={onSelect} />
      ));
  } else {
    body = rows
      .filter((row): row is TSummaryRow => !isLegacyRow(row))
      .map((row) => (
        <ClickSummaryTableRow key={row.id} row={row} onSelect={onSelect} />
      ));
  }

  return (
    <DataTable
      minWidthClassName={mode === 'CLICK' ? 'min-w-[1100px]' : 'min-w-[700px]'}
      headerRow={
        mode === 'CLICK' ? <ClickSummaryTableHeader /> : <LegacyTableHeader />
      }
    >
      {body}
    </DataTable>
  );
}

function ClickSummaryTableHeader() {
  return (
    <tr>
      <TableHeader
        label={summaryManagementStrings.table.startedAt}
        hint={summaryManagementStrings.table.startedAtHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.sourceType}
        hint={summaryManagementStrings.table.sourceTypeHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.status}
        hint={summaryManagementStrings.table.statusHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.sentRecipientCount}
        hint={summaryManagementStrings.table.sentRecipientCountHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.totalClicks}
        hint={summaryManagementStrings.table.totalClicksHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.uniqueClickRecipientCount}
        hint={summaryManagementStrings.table.uniqueClickRecipientCountHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.clickRate}
        hint={summaryManagementStrings.table.clickRateHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.detail}
        hint={summaryManagementStrings.table.detailHint}
      />
    </tr>
  );
}

function LegacyTableHeader() {
  return (
    <tr>
      <TableHeader
        label={summaryManagementStrings.table.startedAt}
        hint={summaryManagementStrings.table.startedAtHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.legacyDeliveryCount}
        hint={summaryManagementStrings.table.legacyDeliveryCountHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.legacyOpenedCount}
        hint={summaryManagementStrings.table.legacyOpenedCountHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.legacyOpenedRate}
        hint={summaryManagementStrings.table.legacyOpenedRateHint}
      />
      <TableHeader
        label={summaryManagementStrings.table.contentConfirm}
        hint={summaryManagementStrings.table.contentConfirmHint}
      />
    </tr>
  );
}

function TableHeader({ label, hint }: { label: string; hint: string }) {
  return (
    <th className="px-2 py-3 text-left leading-tight break-words" title={hint}>
      {label}
    </th>
  );
}

type ClickSummaryTableRowProps = {
  row: TSummaryRow;
  onSelect: SummaryTableProps['onSelect'];
};

function ClickSummaryTableRow({ row, onSelect }: ClickSummaryTableRowProps) {
  const unavailableBadge = resolveUnavailableRecipientBadge(row);
  const unknownBadge = resolveUnknownRecipientBadge(row);
  return (
    <tr className="border-t border-border/60 bg-white transition-colors hover:bg-surface/30">
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatDate(row.startedAt)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <span className="block">
          {summaryManagementStrings.sourceTypes[row.sourceType]}
        </span>
        {row.sourceLabel && (
          <span className="mt-1 block text-[10px] text-muted sm:text-xs">
            {row.sourceLabel}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <div className="flex flex-wrap items-center gap-1">
          <Pill tone={summaryStatusTone[row.status]}>
            <span title={summaryManagementStrings.deliveryStatusHints[row.status]}>
              {summaryManagementStrings.deliveryStatus[row.status]}
            </span>
          </Pill>
          {unavailableBadge && <Pill tone="warning">{unavailableBadge}</Pill>}
          {unknownBadge && <Pill tone="warning">{unknownBadge}</Pill>}
        </div>
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatNumber(row.sentRecipientCount)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <span className="block">{formatNumber(row.totalClicks)}</span>
        {row.coverage !== 'FULL' && (
          <span
            className="mt-1 block text-[10px] font-semibold text-amber-800"
            title={summaryManagementStrings.coverageHints[row.coverage]}
          >
            {summaryManagementStrings.coverageWarnings[row.coverage]}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatSummaryCount(row.uniqueClickRecipientCount)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatSummaryClickRate(row.clickRate)}
      </td>
      <SelectCell row={row} onSelect={onSelect} />
    </tr>
  );
}

type LegacySummaryTableRowProps = {
  row: TLegacySummaryRow;
  onSelect: SummaryTableProps['onSelect'];
};

function LegacySummaryTableRow({ row, onSelect }: LegacySummaryTableRowProps) {
  return (
    <tr className="border-t border-border/60 bg-white transition-colors hover:bg-surface/30">
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatDate(row.sentAt)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatNumber(row.deliveryCount)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatNumber(row.legacyOpenedCount)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatLegacyRate(row)}
      </td>
      <SelectCell row={row} onSelect={onSelect} />
    </tr>
  );
}

function SelectCell({
  row,
  onSelect,
}: {
  row: SummaryDisplayRow;
  onSelect: SummaryTableProps['onSelect'];
}) {
  const isLegacy = isLegacyRow(row);

  return (
    <td className="px-2 py-3 text-left" onClick={(event) => event.stopPropagation()}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          onSelect(row, event);
        }}
      >
        {isLegacy
          ? summaryManagementStrings.table.contentConfirm
          : summaryManagementStrings.table.detail}
      </Button>
    </td>
  );
}
